import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useFieldArray, useFormContext } from "react-hook-form";
import FormInputArchivoReducido, {
	reducedcreateZipFile,
	reducedFileToBase64,
} from "../Inputs/FormInputArchivoReducido";
import FieldsetContainer from "components/blocks/Delta/Inputs/Containers/FieldsetContainer";

interface DocumentSectionItem {
	Seccion: string | number;
	Nombre: string | null;
	Nivel: number;
	Orden?: number;
}

interface DocumentacionArrayItem {
	Nombre: string;
	Extension: string;
	Base64: string;
	DocumentacionSGastosCPId?: string | number;
	Id?: string | null;
	ExternalId?: null | number;
	NombrePortal?: string | null;
	TipoDocumento?: number;
	Nota?: string | null;
	Obligatorio?: boolean;
	UrlPlantilla?: string | null;
	Secciones?: DocumentSectionItem[];
	Orden?: number;
	MaestroDocId?: string | null;
}

export interface AdaptedDocumentacionItem {
	file: File[];
	Ids: {
		Id: string;
		ExternalId: string | number | null;
	};
	NombrePortal: string;
	TipoDocumento: number;
	Nota: string;
	Obligatorio: boolean;
	SasUri: string | null;
	UrlPlantilla: string | null;
	Orden?: number;
	Secciones: DocumentSectionItem[];
	MaestroDocId?: string | null;
	AgrupacionVisual: number | null;
}

interface FieldWithMeta {
	id: string;
	Orden?: number;
	NombrePortal?: string | null;
	Nota?: string | null;
	Obligatorio?: boolean | string | null;
	obligatorio?: boolean | string | null;
	SasUri?: string | null;
	sasUri?: string | null;
	UrlPlantilla?: string | null;
	urlPlantilla?: string | null;
	Secciones?: any[];
	secciones?: any[];
	MaestroDocId?: string | null;
	maestroDocId?: string | null;
	AgrupacionVisual?: number | string | null;
	agrupacionVisual?: number | string | null;
	[key: string]: any;
}

interface SectionEntry {
	key: string;
	sectionId: string | number;
	name: string | null;
	level: number;
	items: Array<{ field: FieldWithMeta; index: number; orden: number }>;
	children: string[];
	minOrden: number;
}

interface ArrayInputArchivoProps {
	name: string;
	urlArchivos?: Array<string | null | undefined>;
	setTotalSize?: React.Dispatch<React.SetStateAction<number>>;
	CondicionAparicionDocumentacion?: Array<number | string | null | undefined> | null;
}

type RenderItemEntry = {
	type: "item";
	field: FieldWithMeta;
	index: number;
	orden: number;
};

type RenderSectionEntry = {
	type: "section";
	sectionKey: string;
	orden: number;
};

type RenderEntry = RenderItemEntry | RenderSectionEntry;

const getFirstDefined = (...values: any[]) =>
	values.find((value) => value !== undefined && value !== null);

const MIS_GESTIONES_SUFFIX = '- disponible en Mis gestiones';
const MODELO_145_URL = '/mis-gestiones/formularios?id=29&origen=2';
const MODELO_145_STATE = {
	id: 29,
	origen: 2,
	titulo: 'Modelo 145',
};

const toBoolean = (value: any): boolean => {
	if (value === true || value === false) return value;
	if (typeof value === "string") {
		const normalized = value.trim().toLowerCase();
		if (normalized === "true") return true;
		if (normalized === "false") return false;
	}
	if (typeof value === "number") return value === 1;
	return false;
};

const toNumberOrDefault = (value: any, defaultValue: number): number => {
	if (value === null || value === undefined || value === "") return defaultValue;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : defaultValue;
};

const resolveAgrupacionVisual = (doc: any): number | null => {
	const rawValue = getFirstDefined(doc?.AgrupacionVisual, doc?.agrupacionVisual, null);
	if (rawValue === null || rawValue === undefined || rawValue === "") return null;
	const parsed = Number(rawValue);
	return Number.isFinite(parsed) ? parsed : null;
};

const buildCondicionAparicionSet = (
	condicionAparicionDocumentacion?: Array<number | string | null | undefined> | null,
): Set<number> => {
	if (!Array.isArray(condicionAparicionDocumentacion)) return new Set<number>();

	return new Set(
		condicionAparicionDocumentacion
			.map((value) => resolveAgrupacionVisual({ AgrupacionVisual: value }))
			.filter((value): value is number => value !== null),
	);
};

const isDocumentVisibleByAgrupacion = (doc: any, condicionAparicionSet: Set<number>): boolean => {
	const agrupacionVisual = resolveAgrupacionVisual(doc);
	return agrupacionVisual === null || condicionAparicionSet.has(agrupacionVisual);
};

const getFilesSizeInMb = (files: any[]): number =>
	files.reduce((sum, file) => {
		const size = typeof file?.size === "number" ? file.size : 0;
		return sum + size / (1024 * 1024);
	}, 0);

const getSectionId = (section: any, fallbackId: string): string | number => {
	const rawSectionId = getFirstDefined(
		section?.Id,
		section?.id,
		section?.Seccion,
		section?.seccion,
		section?.TipoSeccion,
		section?.tipoSeccion,
	);

	if (rawSectionId === null || rawSectionId === undefined || rawSectionId === "") {
		return fallbackId;
	}

	return typeof rawSectionId === "number" ? rawSectionId : String(rawSectionId);
};

const normalizeSections = (doc: any): DocumentSectionItem[] => {
	const rawSections = Array.isArray(doc) ? doc : doc?.Secciones ?? doc?.secciones;
	if (!Array.isArray(rawSections)) return [];

	return rawSections
		.map((section: any, index: number) => {
			const sectionId = getSectionId(section, `section-${index}`);
			const rawName = getFirstDefined(section?.Nombre, section?.nombre, null);
			const parsedLevel = Number(getFirstDefined(section?.Nivel, section?.nivel));
			const level = Number.isFinite(parsedLevel) && parsedLevel > 0 ? parsedLevel : index + 1;
			const order = toNumberOrDefault(getFirstDefined(section?.Orden, section?.orden), Number.MAX_SAFE_INTEGER);

			return {
				Seccion: sectionId,
				Nombre: rawName === null || rawName === undefined || rawName === "" ? null : String(rawName),
				Nivel: level,
				Orden: order,
			};
		})
		.filter(Boolean)
		.sort((a: DocumentSectionItem, b: DocumentSectionItem) => {
			if (a.Nivel !== b.Nivel) return a.Nivel - b.Nivel;
			const leftOrder = toNumberOrDefault(a.Orden, Number.MAX_SAFE_INTEGER);
			const rightOrder = toNumberOrDefault(b.Orden, Number.MAX_SAFE_INTEGER);
			return leftOrder - rightOrder;
		});
};

const sortSectionsByHierarchy = (sections: DocumentSectionItem[]): DocumentSectionItem[] => {
	return [...sections].sort((a, b) => {
		if (a.Nivel !== b.Nivel) return a.Nivel - b.Nivel;
		const leftOrder = toNumberOrDefault(a.Orden, Number.MAX_SAFE_INTEGER);
		const rightOrder = toNumberOrDefault(b.Orden, Number.MAX_SAFE_INTEGER);
		if (leftOrder !== rightOrder) return leftOrder - rightOrder;
		return String(a.Seccion).localeCompare(String(b.Seccion));
	});
};

const isSectionNode = (value: any): boolean => {
	if (!value || typeof value !== "object") return false;
	return (
		Array.isArray(value?.Documentos ?? value?.documentos) ||
		Array.isArray(value?.SubSecciones ?? value?.subSecciones)
	);
};

const flattenDocumentsFromSections = (
	sections: any[],
	parentSections: DocumentSectionItem[] = [],
	fallbackPrefix = "section",
): any[] => {
	if (!Array.isArray(sections)) return [];

	const sortedSections = [...sections].sort((a, b) => {
		const leftOrder = toNumberOrDefault(getFirstDefined(a?.Orden, a?.orden), Number.MAX_SAFE_INTEGER);
		const rightOrder = toNumberOrDefault(getFirstDefined(b?.Orden, b?.orden), Number.MAX_SAFE_INTEGER);
		return leftOrder - rightOrder;
	});

	const flattened: any[] = [];

	sortedSections.forEach((section, sectionIndex) => {
		const level = parentSections.length + 1;
		const sectionOrder = toNumberOrDefault(
			getFirstDefined(section?.Orden, section?.orden),
			Number.MAX_SAFE_INTEGER,
		);
		const sectionId = getSectionId(section, `${fallbackPrefix}-${sectionIndex}-${level}`);
		const rawSectionName = getFirstDefined(section?.Nombre, section?.nombre, null);

		const sectionPath = [
			...parentSections,
			{
				Seccion: sectionId,
				Nombre:
					rawSectionName === null || rawSectionName === undefined || rawSectionName === ""
						? null
						: String(rawSectionName),
				Nivel: level,
				Orden: sectionOrder,
			} as DocumentSectionItem,
		];

		const sectionDocuments = Array.isArray(section?.Documentos ?? section?.documentos)
			? [...(section?.Documentos ?? section?.documentos)]
			: [];

		sectionDocuments
			.sort((a, b) => {
				const leftOrder = toNumberOrDefault(getFirstDefined(a?.Orden, a?.orden), Number.MAX_SAFE_INTEGER);
				const rightOrder = toNumberOrDefault(getFirstDefined(b?.Orden, b?.orden), Number.MAX_SAFE_INTEGER);
				return leftOrder - rightOrder;
			})
			.forEach((documento: any, docIndex: number) => {
				flattened.push({
					...documento,
					Secciones: sectionPath,
					Orden: getFirstDefined(
						documento?.Orden,
						documento?.orden,
						sectionOrder === Number.MAX_SAFE_INTEGER ? docIndex + 1 : sectionOrder + docIndex + 1,
					),
				});
			});

		const nestedSections = Array.isArray(section?.SubSecciones ?? section?.subSecciones)
			? section?.SubSecciones ?? section?.subSecciones
			: [];

		flattened.push(...flattenDocumentsFromSections(nestedSections, sectionPath, `${fallbackPrefix}-${sectionIndex}`));
	});

	return flattened;
};

const extractDocumentsForAdaptation = (docs: any[]): any[] => {
	if (!Array.isArray(docs) || docs.length === 0) return [];
	const hasOnlySections = docs.every(isSectionNode);
	if (!hasOnlySections) return docs;
	return flattenDocumentsFromSections(docs);
};

const resolveDocumentId = (doc: any): string => {
	const rawId = getFirstDefined(doc?.Id, doc?.id, "");
	if (rawId === null || rawId === undefined) return "";
	return String(rawId);
};

const resolveMaestroDocId = (doc: any): string | null => {
	const rawValue = getFirstDefined(doc?.MaestroDocId, doc?.maestroDocId, null);
	if (rawValue === null || rawValue === undefined || rawValue === "") return null;
	return String(rawValue);
};

const resolveExternalId = (doc: any): string | number | null => {
	const rawValue = getFirstDefined(doc?.ExternalId, doc?.externalId, doc?.Ids?.ExternalId, null);
	return rawValue === undefined ? null : rawValue;
};

const resolveSasUri = (doc: any): string | null => {
	const rawValue = getFirstDefined(doc?.SasUri, doc?.sasUri, doc?.Uri, doc?.uri, null);
	if (typeof rawValue !== "string") return null;
	return rawValue.trim() ? rawValue : null;
};

const resolveUrlPlantilla = (doc: any): string | null => {
	const rawValue = getFirstDefined(doc?.SasUriPlantilla, doc?.SasUriPlantilla, null);
	if (typeof rawValue !== "string") return null;
	return rawValue.trim() ? rawValue : null;
};

const resolveOutputExternalId = (doc: any): number => {
	const rawValue = getFirstDefined(doc?.Ids?.ExternalId, doc?.ExternalId, doc?.externalId, 0);
	if (typeof rawValue === "string" && rawValue.trim() === "") return 0;
	const parsed = Number(rawValue);
	return Number.isFinite(parsed) ? parsed : 0;
};

const resolveOutputId = (doc: any): string | null => {
	const rawValue = getFirstDefined(doc?.Ids?.Id, doc?.Id, doc?.id, null);
	if (rawValue === null || rawValue === undefined || rawValue === "") return null;
	return String(rawValue);
};

const resolveOutputMaestroDocId = (doc: any): string | null => {
	const rawValue = getFirstDefined(doc?.MaestroDocId, doc?.maestroDocId, null);
	if (rawValue === null || rawValue === undefined || rawValue === "") return null;
	return String(rawValue);
};

export const adaptDocumentacion = (docs: any): AdaptedDocumentacionItem[] => {
	if (!Array.isArray(docs)) return [];

	const docsToAdapt = extractDocumentsForAdaptation(docs);

	const adaptedDocs = docsToAdapt.map((doc: any, index: number) => {
		const id = resolveDocumentId(doc);
		const externalId = resolveExternalId(doc);
		const nombrePortal = getFirstDefined(doc?.NombrePortal, doc?.nombrePortal, doc?.Nombre, doc?.nombre, "");
		const tipoDocumento = getFirstDefined(doc?.TipoDocumento, doc?.tipoDocumento, 0);
		const nota = getFirstDefined(doc?.Nota, doc?.nota, "");
		const obligatorio = getFirstDefined(doc?.Obligatorio, doc?.obligatorio, false);
		const orden = toNumberOrDefault(getFirstDefined(doc?.Orden, doc?.orden), index + 1);

		return {
			file: [],
			Ids: {
				Id: id,
				ExternalId: externalId ?? null,
			},
			NombrePortal: String(nombrePortal ?? ""),
			TipoDocumento: toNumberOrDefault(tipoDocumento, 0),
			Nota: String(nota ?? ""),
			Obligatorio: toBoolean(obligatorio),
			SasUri: resolveSasUri(doc),
			UrlPlantilla: resolveUrlPlantilla(doc),
			Orden: orden,
			Secciones: normalizeSections(doc),
			MaestroDocId: resolveMaestroDocId(doc),
			AgrupacionVisual: resolveAgrupacionVisual(doc),
		};
	});

	return adaptedDocs.sort(
		(a, b) => toNumberOrDefault(a.Orden, Number.MAX_SAFE_INTEGER) - toNumberOrDefault(b.Orden, Number.MAX_SAFE_INTEGER),
	);
};

export const prepareDocumentacionArray = async (docs: any): Promise<DocumentacionArrayItem[]> => {
	if (!Array.isArray(docs)) return [];

	const docArrayPromises = docs.map(async (d) => {
		const Secciones = normalizeSections(d);
		const files = Array.isArray(d?.file) ? d.file : [];
		const outputId = resolveOutputId(d);
		const outputExternalId = resolveOutputExternalId(d);
		const maestroDocId = resolveOutputMaestroDocId(d);

		if (files.length > 0) {
			if (files.length > 1) {
				return {
					Nombre: "DocumentacionZipPortal.zip",
					Extension: ".zip",
					Base64: await reducedcreateZipFile(files),
					DocumentacionSGastosCPId: d?.Ids?.IdExternal,
					Id: outputId,
					ExternalId: outputExternalId,
					NombrePortal: d?.NombrePortal,
					TipoDocumento: toNumberOrDefault(d?.TipoDocumento, 0),
					Secciones,
					Orden: d?.Orden === undefined ? undefined : toNumberOrDefault(d?.Orden, 0),
					MaestroDocId: maestroDocId,
				};
			}

			const ext = files[0].name.slice(files[0].name.lastIndexOf("."));
			return {
				Nombre: files[0].name,
				Extension: ext || "",
				Base64: (await reducedFileToBase64(files[0])) || "",
				DocumentacionSGastosCPId: d?.Ids?.IdExternal,
				Id: outputId,
				ExternalId: outputExternalId,
				NombrePortal: d?.NombrePortal,
				TipoDocumento: toNumberOrDefault(d?.TipoDocumento, 0),
				Nota: d?.Nota,
				Secciones,
				Orden: d?.Orden === undefined ? undefined : toNumberOrDefault(d?.Orden, 0),
				MaestroDocId: maestroDocId,
			};
		}

		return null;
	});

	const docArray = await Promise.all(docArrayPromises);
	return docArray.filter((d) => d !== null) as DocumentacionArrayItem[];
};

export const prepareDocumentacionArrayWithCase = async (docs: any): Promise<DocumentacionArrayItem[]> => {
	if (!Array.isArray(docs)) return [];

	const docArrayPromises = docs.map(async (d) => {
		const files = Array.isArray(d?.file)
			? d.file.filter((file: any) => file && typeof file.name === "string")
			: [];
		const outputId = resolveOutputId(d);
		const maestroDocId = resolveOutputMaestroDocId(d);
		const normalizedId = outputId || null;

		// Cuando hay varios archivos se envía un ZIP como un único documento.

		if (files.length > 1) {
			return {
				Nombre: "DocumentacionZipPortal.zip",
				Extension: "zip",
				Base64: await reducedcreateZipFile(files),
				Id: normalizedId,
				MaestroDocId: maestroDocId,
			};
		}

		if (files.length === 1) {
			const file = files[0];
			const dotIndex = file.name.lastIndexOf(".");
			const ext = dotIndex >= 0 ? file.name.slice(dotIndex + 1) : "";

			return {
				Nombre: file.name,
				Extension: ext,
				Base64: (await reducedFileToBase64(file)) || "",
				Id: normalizedId,
				MaestroDocId: maestroDocId,
			};
		}

		return {
			Nombre: "",
			Extension: "",
			Base64: "",
			Id: normalizedId,
			MaestroDocId: maestroDocId,
		};
	});

	const docArray = await Promise.all(docArrayPromises);
	return docArray as DocumentacionArrayItem[];
};

const ArrayInputArchivo: React.FC<ArrayInputArchivoProps> = ({
	name,
	urlArchivos = [],
	setTotalSize,
	CondicionAparicionDocumentacion = null,
}) => {
	const { clearErrors, control, getValues, setValue } = useFormContext();

	const { fields } = useFieldArray({
		control,
		name,
	});

	const condicionAparicionKey = useMemo(
		() =>
			Array.from(buildCondicionAparicionSet(CondicionAparicionDocumentacion))
				.sort((a, b) => a - b)
				.join("|"),
		[CondicionAparicionDocumentacion],
	);

	const condicionAparicionSet = useMemo(
		() =>
			new Set(
				condicionAparicionKey
					? condicionAparicionKey
							.split("|")
							.map((value) => Number(value))
							.filter((value) => Number.isFinite(value))
					: [],
			),
		[condicionAparicionKey],
	);

	useEffect(() => {
		const typedFields = fields as unknown as FieldWithMeta[];
		const documents = getValues(name);
		let removedSize = 0;

		typedFields.forEach((field, index) => {
			if (isDocumentVisibleByAgrupacion(field, condicionAparicionSet)) return;

			const files = Array.isArray(documents?.[index]?.file) ? documents[index].file : [];
			if (files.length > 0) {
				removedSize += getFilesSizeInMb(files);
				setValue(`${name}.${index}.file`, [], {
					shouldDirty: true,
					shouldTouch: true,
					shouldValidate: false,
				});
			}

			clearErrors(`${name}.${index}.file`);
		});

		if (removedSize > 0 && typeof setTotalSize === "function") {
			setTotalSize((prev) => Math.max(0, prev - removedSize));
		}
	}, [clearErrors, condicionAparicionKey, condicionAparicionSet, fields, getValues, name, setTotalSize, setValue]);

	const groupedFields = useMemo<{
		orderedRenderList: RenderEntry[];
		sectionMap: Map<string, SectionEntry>;
	}>(() => {
		const typedFields = (fields as unknown as FieldWithMeta[])
			.map((field, index) => ({ field, index }))
			.filter(({ field }) => isDocumentVisibleByAgrupacion(field, condicionAparicionSet));

		const withNormalizedSections = typedFields
			.map(({ field, index }) => ({
				field,
				index,
				orden: toNumberOrDefault(field?.Orden, index + 1),
				sections: normalizeSections(field),
			}))
			.sort((a, b) => a.orden - b.orden);

		const hasSectionSystem = withNormalizedSections.some((item) => item.sections.length > 0);

		if (!hasSectionSystem) {
			return {
				orderedRenderList: withNormalizedSections.map(({ field, index, orden }) => ({
					type: "item" as const,
					field,
					index,
					orden,
				})),
				sectionMap: new Map<string, SectionEntry>(),
			};
		}

		const sectionMap = new Map<string, SectionEntry>();
		const rootSectionKeys = new Set<string>();

		withNormalizedSections.forEach(({ sections, orden }) => {
			let parentKey: string | null = null;

			sortSectionsByHierarchy(sections).forEach((section, sectionIndex) => {
				const sectionKey = `${parentKey ?? "__root__"}::${String(section.Seccion)}`;
				const sectionOrder = toNumberOrDefault(section.Orden, Number.MAX_SAFE_INTEGER);
				const level = Number.isFinite(Number(section.Nivel))
					? Math.max(1, Number(section.Nivel))
					: sectionIndex + 1;
				const existing = sectionMap.get(sectionKey);

				if (!existing) {
					sectionMap.set(sectionKey, {
						key: sectionKey,
						sectionId: section.Seccion,
						name: section.Nombre,
						level,
						items: [],
						children: [],
						minOrden: Math.min(orden, sectionOrder),
					});

					if (parentKey === null) {
						rootSectionKeys.add(sectionKey);
					} else {
						const parent = sectionMap.get(parentKey);
						if (parent && !parent.children.includes(sectionKey)) {
							parent.children.push(sectionKey);
						}
					}
				} else {
					if (!existing.name && section.Nombre) {
						existing.name = section.Nombre;
					}
					if (level < existing.level) {
						existing.level = level;
					}
					existing.minOrden = Math.min(existing.minOrden, orden, sectionOrder);

					if (parentKey === null) {
						rootSectionKeys.add(sectionKey);
					} else {
						const parent = sectionMap.get(parentKey);
						if (parent && !parent.children.includes(sectionKey)) {
							parent.children.push(sectionKey);
						}
					}
				}

				parentKey = sectionKey;
			});
		});

		withNormalizedSections.forEach(({ field, index, sections, orden }) => {
			if (sections.length === 0) return;

			let parentKey: string | null = null;
			sortSectionsByHierarchy(sections).forEach((section) => {
				parentKey = `${parentKey ?? "__root__"}::${String(section.Seccion)}`;
			});

			if (!parentKey) return;

			const leafSection = sectionMap.get(parentKey);
			if (leafSection) {
				leafSection.items.push({ field, index, orden });
				leafSection.minOrden = Math.min(leafSection.minOrden, orden);
			}
		});

		sectionMap.forEach((section) => {
			section.items.sort((a, b) => a.orden - b.orden);
			section.children.sort((leftKey, rightKey) => {
				const left = sectionMap.get(leftKey);
				const right = sectionMap.get(rightKey);
				const leftOrder = left?.minOrden ?? Number.MAX_SAFE_INTEGER;
				const rightOrder = right?.minOrden ?? Number.MAX_SAFE_INTEGER;
				if (leftOrder !== rightOrder) return leftOrder - rightOrder;
				return String(left?.sectionId ?? "").localeCompare(String(right?.sectionId ?? ""));
			});
		});

		const flatItemsWithOrden: RenderItemEntry[] = withNormalizedSections
			.filter((item) => item.sections.length === 0)
			.map(({ field, index, orden }) => ({
				type: "item",
				field,
				index,
				orden,
			}));

		const rootSectionsWithOrden: RenderSectionEntry[] = Array.from(rootSectionKeys).map((sectionKey) => ({
			type: "section",
			sectionKey,
			orden: sectionMap.get(sectionKey)?.minOrden ?? Number.MAX_SAFE_INTEGER,
		}));

		const orderedRenderList: RenderEntry[] = [...flatItemsWithOrden, ...rootSectionsWithOrden]
			.sort((a, b) => a.orden - b.orden);

		return {
			orderedRenderList,
			sectionMap,
		};
	}, [condicionAparicionKey, condicionAparicionSet, fields]);

	const renderInput = ({ field, index }: { field: FieldWithMeta; index: number }) => {
		const rawLabel = field?.NombrePortal || `Documento ${index + 1}`;
		const label =
			typeof rawLabel === 'string' && rawLabel.includes(MIS_GESTIONES_SUFFIX)
				? (
					<>
						{rawLabel.replace(MIS_GESTIONES_SUFFIX, '')}
						<span className="document-label-link-prefix">- disponible en </span>
						<Link
							to={MODELO_145_URL}
							state={MODELO_145_STATE}
							className="document-label-highlight"
						>
							Mis gestiones
						</Link>
					</>
				)
				: rawLabel;
		const rawObligatorio = field?.Obligatorio ?? field?.obligatorio ?? false;
		const isObligatorio =
			rawObligatorio === true ||
			(typeof rawObligatorio === "string" && rawObligatorio.toLowerCase() === "true");

		const existingSasUriByIndex = Array.isArray(urlArchivos) ? urlArchivos[index] : "";
		const existingSasUri =
			(typeof existingSasUriByIndex === "string" && existingSasUriByIndex.trim()) ||
			(typeof field?.SasUri === "string" && field.SasUri.trim()) ||
			(typeof field?.sasUri === "string" && field.sasUri.trim()) ||
			"";

		const existingUrlPlantilla =
			(typeof field?.UrlPlantilla === "string" && field.UrlPlantilla.trim()) ||
			(typeof field?.urlPlantilla === "string" && field.urlPlantilla.trim()) ||
			"";

		const requiredMessage =
			isObligatorio && !existingSasUri
				? `Debe añadir al menos un archivo a ${rawLabel}`
				: undefined;

		const handleSetTotalSizeDelta =
			typeof setTotalSize === "function"
				? (delta: number) => setTotalSize((prev) => prev + delta)
				: undefined;

		return (
			<FormInputArchivoReducido
				key={field.id || `${name}-${index}`}
				name={`${name}.${index}.file`}
				label={label}
				disabled={false}
				required={requiredMessage}
				xl={12}
				md={12}
				setTotalSize={handleSetTotalSizeDelta}
				urlArchivo={existingSasUri}
				urlPlantilla={existingUrlPlantilla}
				nota={field?.Nota || ""}
			/>
		);
	};

	const renderSection = (sectionKey: string): React.ReactNode => {
		const section = groupedFields.sectionMap.get(sectionKey);
		if (!section) return null;

		const title = section.name ? String(section.name) : "";
		const children = section.children.map((childKey) => renderSection(childKey));

		if (!title) {
			return (
				<React.Fragment key={`section-${section.key}`}>
					{section.items.map(renderInput)}
					{children}
				</React.Fragment>
			);
		}

		if (section.level === 1) {
			return (
				<FieldsetContainer key={`section-${section.key}`} legend={title}>
					{section.items.map(renderInput)}
					{children}
				</FieldsetContainer>
			);
		}

		return (
			<div key={`section-${section.key}`} style={{ marginLeft: `${Math.max(0, section.level - 1) * 16}px` }}>
				{title ? (
					<div style={{ fontWeight: 700, marginTop: "12px", marginBottom: "8px", fontSize: "18px" }}>
						{title}
					</div>
				) : null}
				{section.items.map(renderInput)}
				{children}
			</div>
		);
	};

	return (
		<>
			{groupedFields.orderedRenderList.map((entry) => {
				if (entry.type === "item") return renderInput(entry);
				if (entry.type === "section") return renderSection(entry.sectionKey);
				return null;
			})}
		</>
	);
};

export default ArrayInputArchivo;
